import { useState, useEffect } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { formatHtml } from 'sanitize-text/sanitizers/HtmlSanitizer';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import SVGClose from 'visitor-ui-component-library-icons/icons/SVGClose';
import VizExLoadingButton from 'visitor-ui-component-library/button/VizExLoadingButton';
import { createThemeV2 } from 'visitor-ui-component-library/theme/createThemeV2';
import { OBSIDIAN, WHITE, NEUTRAL_400, NEUTRAL_800, NEUTRAL_1600 } from 'visitor-ui-component-library/constants/WidgetColors';
import { darken, getCanReadTextOnce } from 'visitor-ui-component-library/utils/colors';
import { getFocusRingStyles } from 'visitor-ui-component-library/utils/getFocusRingStyles';
import { EXTRA_EXTRA_SMALL, EXTRA_SMALL } from 'visitor-ui-component-library/constants/sizes';
import VizExSmall from 'visitor-ui-component-library/typography/VizExSmall';
import { useChatWidgetLocale } from '../ChatWidgetLocaleContext';
import { WELCOME_MESSAGE, CHAT_THREAD, SPOTLIGHT } from '../constants/consentPromptVariants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ConsentContainer = styled.div.withConfig({
  displayName: "ConsentPromptV2__ConsentContainer"
})(["display:flex;flex-direction:column;gap:8px;width:100%;position:relative;margin-top:", ";margin-bottom:", ";"], ({
  $variant,
  $usePillLauncher
}) => $variant === WELCOME_MESSAGE && !$usePillLauncher ? '16px' : '0', ({
  $variant,
  $isAIChatBot
}) => $variant !== WELCOME_MESSAGE && $isAIChatBot ? '8px' : '0');
const ConsentDivider = styled.hr.withConfig({
  displayName: "ConsentPromptV2__ConsentDivider"
})(["margin:0;width:100%;border:none;border-top:1px solid ", ";"], NEUTRAL_400);
const ConsentText = styled(VizExSmall).withConfig({
  displayName: "ConsentPromptV2__ConsentText"
})(["display:block;text-align:left;font-weight:300;font-size:12px;color:", ";line-height:", ";padding-right:", ";p{margin:0;}"], NEUTRAL_1600, ({
  $variant
}) => {
  if ($variant === SPOTLIGHT) return '14px';
  return $variant === WELCOME_MESSAGE ? '18px' : '21px';
}, ({
  $hasCloseButton
}) => $hasCloseButton ? '32px' : '0');
const BaseConsentButton = styled(VizExButton).withConfig({
  displayName: "ConsentPromptV2__BaseConsentButton"
})(["align-self:flex-start;font-weight:300;"]);
const WelcomeMessageButton = styled(BaseConsentButton).withConfig({
  displayName: "ConsentPromptV2__WelcomeMessageButton"
})(["height:24px;min-height:24px;padding:0 12px;font-size:12px;line-height:14px;"]);
const ThreadViewButton = styled(BaseConsentButton).withConfig({
  displayName: "ConsentPromptV2__ThreadViewButton"
})(["height:32px;min-height:32px;padding:0 16px;font-size:13px;line-height:18px;"]);
const SpotlightConsentButton = styled.button.withConfig({
  displayName: "ConsentPromptV2__SpotlightConsentButton"
})(["display:flex;align-items:center;justify-content:center;align-self:flex-start;height:32px;padding:0 16px;font-size:14px;font-weight:400;line-height:20px;letter-spacing:0;border:1px solid ", ";border-radius:9999px;background-color:transparent;color:", ";cursor:pointer;transition:background-color 150ms ease;&:hover{background-color:", ";}", ""], NEUTRAL_800, NEUTRAL_1600, darken(WHITE, 0.08), getFocusRingStyles({
  ringColor: NEUTRAL_1600
}));
const SpamProtectionText = styled.div.withConfig({
  displayName: "ConsentPromptV2__SpamProtectionText"
})(["font-size:12px;line-height:18px;font-weight:500;color:", ";"], NEUTRAL_1600);
const CloseButtonWrapper = styled.div.withConfig({
  displayName: "ConsentPromptV2__CloseButtonWrapper"
})(["position:absolute;top:-2px;right:2px;"]);
const ConsentPromptV2 = ({
  variant = WELCOME_MESSAGE,
  consentMessage,
  showConsentButton = false,
  onConsentAccept,
  onClose,
  showSpamProtection = false,
  accentColor,
  showDivider = true,
  isAIChatBot = false,
  isPortal53 = false,
  usePillLauncher = false
}) => {
  const locale = useChatWidgetLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    setIsSubmitting(false);
  }, [consentMessage]);
  const handleConsentClick = event => {
    event.stopPropagation();
    if (onConsentAccept) {
      setIsSubmitting(true);
      onConsentAccept();
    }
  };
  const isTextReadable = accentColor ? getCanReadTextOnce(accentColor) : false;
  const primaryColor = isTextReadable ? accentColor : OBSIDIAN;
  const spinnerSize = variant === WELCOME_MESSAGE ? EXTRA_EXTRA_SMALL : EXTRA_SMALL;
  const showCloseButton = variant === CHAT_THREAD && !showConsentButton && isPortal53 && !!onClose;
  const getConsentButtonComponent = () => {
    if (variant === SPOTLIGHT) return SpotlightConsentButton;
    if (variant === WELCOME_MESSAGE) return WelcomeMessageButton;
    return ThreadViewButton;
  };
  return /*#__PURE__*/_jsxs(ConsentContainer, {
    $variant: variant,
    $isAIChatBot: isAIChatBot,
    $usePillLauncher: usePillLauncher,
    "data-test-id": "consent-prompt",
    children: [showCloseButton && /*#__PURE__*/_jsx(CloseButtonWrapper, {
      children: /*#__PURE__*/_jsx(VizExIconButton, {
        size: "sm",
        use: "transparent-on-background",
        onClick: onClose,
        theme: createThemeV2({
          colors: {
            text: NEUTRAL_1600
          }
        }),
        "data-test-id": "consent-prompt-close-button",
        "aria-label": I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.dismiss'),
        children: /*#__PURE__*/_jsx(VizExIcon, {
          size: "xs",
          icon: /*#__PURE__*/_jsx(SVGClose, {})
        })
      })
    }), showDivider && /*#__PURE__*/_jsx(ConsentDivider, {}), consentMessage && /*#__PURE__*/_jsx(ConsentText, {
      $variant: variant,
      $hasCloseButton: showCloseButton,
      use: "help",
      "data-test-id": "consent-prompt-text",
      dangerouslySetInnerHTML: {
        __html: formatHtml(consentMessage)
      },
      onClick: event => {
        if (event.target.closest('a')) {
          event.stopPropagation();
        }
      }
    }), showConsentButton && /*#__PURE__*/_jsx(VizExLoadingButton, {
      currentState: isSubmitting ? 'submitting' : 'ready',
      use: variant === SPOTLIGHT ? 'secondary' : undefined,
      Button: getConsentButtonComponent(),
      spinnerSize: spinnerSize,
      onClick: handleConsentClick,
      theme: createThemeV2({
        colors: {
          primary: variant === SPOTLIGHT ? NEUTRAL_1600 : primaryColor
        }
      }),
      "data-test-id": "consent-prompt-accept-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-experience-components.buttons.agree",
        options: {
          locale
        }
      })
    }), showSpamProtection && /*#__PURE__*/_jsx(SpamProtectionText, {
      "data-test-id": "consent-prompt-spam-protection",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-experience-components.spamProtectionEnabled",
        options: {
          locale
        }
      })
    })]
  });
};
ConsentPromptV2.displayName = 'ConsentPromptV2';
export default ConsentPromptV2;